import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, QrCode, UserPlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, Button, cn } from "@tabipla/ui";
import { ConfirmDialog } from "@tabipla/components";
import { useConfirmDialog } from "@tabipla/hooks/confirm-dialog";
import { DashboardLayout } from "@/presentation/layouts/DashboardLayout";
import { useFriends, useRemoveFriend } from "@/application/hooks/useFriends";
import { FriendCodeDialog } from "@/presentation/components/friends/FriendCodeDialog";
import { AddFriendDialog } from "@/presentation/components/friends/AddFriendDialog";

export function FriendsPage() {
  const { t } = useTranslation();
  const { confirm, dialogProps } = useConfirmDialog();
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const { data: friends = [], isLoading, error } = useFriends();

  const removeFriend = useRemoveFriend({
    onSuccess: () => {
      toast.success(t("friends.remove.success", "友達を削除しました"));
    },
    onError: (e) => {
      toast.error(e.message);
    },
  });

  const handleRemoveFriend = async (friendId: string, friendName: string) => {
    const confirmed = await confirm({
      title: t("friends.remove.title", "友達を削除"),
      description: t("friends.remove.description", "{{name}}を友達から削除しますか？", {
        name: friendName,
      }),
      confirmText: t("common.delete", "削除"),
      cancelText: t("common.cancel", "キャンセル"),
      destructive: true,
    });

    if (confirmed) {
      removeFriend.mutate(friendId);
    }
  };

  return (
    <DashboardLayout title={t("friends.title", "友達")}>
      <div className="animate-fade-in space-y-6">
        {/* Header - Desktop only */}
        <div className="hidden lg:block">
          <h1 className="text-2xl font-bold tracking-tight">{t("friends.title", "友達")}</h1>
          <p className="text-muted-foreground">
            {t("friends.subtitle", "友達を追加して、旅行に招待しましょう")}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCodeDialog(true)} className="flex-1">
            <QrCode className="mr-2 h-4 w-4" />
            {t("friends.showMyCode", "マイコード")}
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="flex-1">
            <UserPlus className="mr-2 h-4 w-4" />
            {t("friends.addFriend", "友達追加")}
          </Button>
        </div>

        {/* Friends List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <Card>
              <CardContent className="py-8 text-center text-destructive">
                {t("friends.error", "友達リストの取得に失敗しました")}
              </CardContent>
            </Card>
          ) : friends.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-4 py-12">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                  <Users className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="text-center">
                  <p className="font-medium">{t("friends.empty.title", "友達がいません")}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("friends.empty.description", "友達を追加して旅行計画を共有しましょう")}
                  </p>
                </div>
                <Button onClick={() => setShowAddDialog(true)} size="sm">
                  <UserPlus className="mr-2 h-4 w-4" />
                  {t("friends.addFriend", "友達追加")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            friends.map((friend) => (
              <Card key={friend.id} className="overflow-hidden transition-all hover:shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div
                      className={cn(
                        "flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg",
                        friend.avatarColor
                          ? `bg-gradient-to-br ${friend.avatarColor}`
                          : "bg-primary/10"
                      )}
                    >
                      {friend.avatarEmoji ||
                        (friend.image ? (
                          <img
                            src={friend.image}
                            alt={friend.name}
                            className="h-full w-full rounded-full object-cover"
                          />
                        ) : (
                          <Users className="h-6 w-6 text-primary" />
                        ))}
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{friend.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t("friends.addedAt", "追加日: {{date}}", {
                          date: new Date(friend.createdAt).toLocaleDateString(),
                        })}
                      </p>
                    </div>

                    {/* Actions */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveFriend(friend.friendId, friend.name)}
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      disabled={removeFriend.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Dialogs */}
      <FriendCodeDialog open={showCodeDialog} onOpenChange={setShowCodeDialog} />
      <AddFriendDialog open={showAddDialog} onOpenChange={setShowAddDialog} />
      <ConfirmDialog {...dialogProps} />
    </DashboardLayout>
  );
}
